<template>
  <MarketCard>
    <div :class="classes.cardHeader">
      <CardHeader
        :eventId="eventId"
        :class="[classes.datesport, 'strapline']"
      />
    </div>

    <div :class="classes.contentWrapper">
      <EventNames :class="classes.leftContent" :eventId="eventId" />

      <div :class="classes.marketWrapper">
        <div :class="classes.marketNamesWrapper">
          <DynamicText
            v-for="name in ['Moneyline', 'Spread', 'Totals']"
            :id="marketBranding?.marketNameFont"
            :key="name"
            :class="classes.marketTitle"
            tag="span"
            styleGroup="Bet Market"
            :get="marketNameStyle.get"
            :set="marketNameStyle.set"
            class="market-name"
            >{{ name }}</DynamicText
          >
        </div>
        <div :class="classes.markets">
          <Button
            v-for="(market, index) in homeAwayOptions"
            :key="market?.marketId || index"
            :stagger="index"
            :class="classes.marketButton"
            :optionId="market?.options[0]"
            :marketId="market?.marketId"
            class="home-button"
          />
        </div>

        <div :class="classes.markets">
          <Button
            v-for="(market, index) in homeAwayOptions"
            :key="market?.marketId || index"
            :stagger="index + 3"
            :class="classes.marketButton"
            :optionId="market?.options[market?.options.length - 1]"
            :marketId="market?.marketId"
            class="away-button"
          />
        </div>
      </div>
    </div>
  </MarketCard>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ComputedRef } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { AdTemplateMarket, Brand } from "@fresh8/types/src/index";

import Button from "../Button.vue";
import EventNames from "../../../EventNamesBadgesStacked.vue";
import CardHeader from "../../../CardHeaderLeaderboard.vue";

import {
  composeEvent,
  composeHomeAwayOptions,
  composeMultiFtrMarkets
} from "@fresh8/ad-data-composition/src/betData";
import composeDate from "@fresh8/ad-data-composition/src/date";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import MarketCard from "../../../styled/MarketCard.vue";
import DynamicText from "../../../DynamicText.vue";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { SportEvent } from "@fresh8/types/src/betData";

export default defineComponent({
  name: "MultiFtrStraplineCard",
  components: {
    Button,
    CardHeader,
    MarketCard,
    DynamicText,
    EventNames
  },
  props: {
    marketBranding: {
      type: Object as PropType<AdTemplateMarket>,
      required: true
    },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const { adTemplateBranding } = injectBrand();
    const carouselContext = injectCarouselContext();
    const marketIds = composeMultiFtrMarkets(props.eventId);
    const homeAwayOptions = composeHomeAwayOptions(marketIds);
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const fixtureStartDate = composeDate(event.value?.id);

    const reactiveStyles = computed(() => ({
      ...adTemplateBranding.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const classesRef = useMultiFtrCardStyles(reactiveStyles);
    return {
      marketIds,
      homeAwayOptions,
      event,
      fixtureStartDate,
      classes: classesRef.value,
      marketNameStyle: {
        get(brand: Brand | undefined) {
          return brand && brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureTimeFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      fixtureNameStyle: {
        get: (brand: Brand) => brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});

const useMultiFtrCardStyles = createUseStyles({
  cardHeaderWrapper: {
    display: "flex",
    flex: "1 1 auto",
    height: "16px"
  },
  cardHeader: {
    position: "absolute",
    width: "40%"
  },
  datesport: {
    minWidth: "133px",
    "& span": {
      fontSize: "10px !important"
    }
  },
  marketNamesWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "flex-start",
    height: "100%",
    maxHeight: "12px",
    marginBottom: "2px"
  },
  marketTitle: {
    fontSize: "10px",
    textAlign: "center",
    height: "12px",
    flex: 1,
    marginLeft: "3px",
    paddingTop: "4px",
    "&:last-of-type": {
      marginRight: "4px"
    }
  },
  leftContent: {
    height: "100%",
    justifyContent: "flex-end !important",
    paddingBottom: "9px !important",
    paddingTop: "25px"
  },
  contentWrapper: {
    display: "flex",
    height: "100%",
    width: "100%",

    flex: 1,

    alignItems: "center"
  },

  marketWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    flex: "1 0 50%",
    marginBottom: "2px"
  },
  markets: {
    display: "flex",
    height: "100%",
    flexDirection: "row",
    justifyContent: "space-around",
    margin: "6px 3px 2px",

    "&:last-of-type": {
      padding: "0 0 4px",
      marginTop: "2px",
      marginBottom: "0"
    }
  },

  marketButton: {
    height: "100% !important",
    minHeight: "0px",
    flex: "1",
    margin: "0 2px",
    "& .odds-text": {
      fontSize: "11px"
    },
    "& .meta-text": {
      fontSize: "10px"
    }
  },
  live: {
    color: "red"
  }
});
</script>
